import { useEffect, useRef, useState } from 'react';
import { BriefingFact, CreateOrderRequest, DateKst, OrderDto } from '../../types/opsBriefing';
import { OrderView, toOrderViews } from './adapters';
import CoachOrderPreview from './CoachOrderPreview';
import { VALUE_UNAVAILABLE_TEXT, VALUE_UNAVAILABLE_REASON } from './memberValue';
import { OpsContext } from './useOpsContext';
import { useCreateOpsOrderMutation, useOpsOrdersQuery } from '../../services/useOpsBriefingService';

/**
 * 지시 보내기 — 확인 단계.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 한 단계를 더 넣었나
 * ─────────────────────────────────────────────────────────────
 * 지시는 누르는 즉시 서버로 나갔습니다. 그래서 세 가지가 안 됐습니다.
 *   · 어떤 문장이 강사에게 가는지 누르기 전에 볼 수 없었다
 *   · 잘못 눌러도 되돌릴 수 없었다 (연타 가드만 있었다)
 *   · 성공 토스트가 사라지면 보냈는지 알 방법이 없었다
 *
 * 이 창은 "귀찮은 관문"이 아니라 **마지막 눈**입니다. 그래서
 *   · 누구에게 · 어느 회원 건 · 무슨 문장이 가는지를 요약 없이 그대로 보여주고
 *   · 그 자리에서 강사에게 덧붙일 말을 적을 수 있고
 *   · 이미 같은 회원에게 보낸 지시가 있으면 먼저 알려주고
 *   · 열자마자 주 버튼에 포커스가 가서 Enter 한 번으로 끝납니다 (Esc 로 취소)
 *
 * 그리고 **강사 화면 미리보기**가 옆에 섭니다. 값의 목록("무슨 문장이 간다")과
 * 실제 화면("강사가 열면 뭘 본다")은 다르게 읽힙니다 — 특히 문구 자리가 비어 있을
 * 때 그렇습니다. 여기서 적는 글은 그 자리에서 같이 바뀝니다 (CoachOrderPreview).
 *
 * ⚠️ **이 창은 회원에게 보낼 문구를 다루지 않습니다** (2026-08 대표 결정).
 *    지시는 강사에게 가는 것이고, 회원에게 무슨 말을 할지는 강사가 정합니다.
 *    한때 여기에 「회원에게 보낼 문구」 칸이 있었는데, 매일 아침 쓰는 화면에서
 *    원장이 회원 인사말까지 쓰고 앉아 있게 만드는 자리였습니다.
 *    회원 현황에서 낸 지시는 `memberDraft` 를 아예 싣지 않습니다 —
 *    그러면 강사 앱이 회원 이름과 근거로 첫 마디를 만들어 줍니다(기존 동작).
 *
 * ⚠️ 두 화면(오늘의 브리핑 · 회원 현황)이 **이 훅 하나만** 씁니다.
 *    각자 확인 창을 만들면 원장님이 화면마다 다른 흐름을 겪습니다.
 *
 * ⚠️ 체크박스(완료 기록) 경로는 여기를 지나지 않습니다. 그건 지시를 만들지 않습니다.
 *    (OpsBriefingTodayPage.handleMarkDone 주석 참고)
 */

const BORDER = '#E3EAF3';
const NAVY = '#1A305D';

/**
 * 서버 상한. 넘기면 400 이 난다 (BriefingCard 의 textarea 와 같은 값).
 * ownerNote 와 memberDraft 가 같은 1000 자다 — 서버가 일부러 같게 뒀다
 * (opsOrder.model memberDraft 주석). 여기만 좁히면 지금까지 통과하던 길이가
 * 조용히 막힌다.
 */
const TEXT_MAX = 1000;

/**
 * 강사 앱 알림 안내.
 *
 * ─────────────────────────────────────────────────────────────
 * 2026-08-26 부터 푸시가 실제로 나갑니다
 * ─────────────────────────────────────────────────────────────
 * 그 전에는 `'강사 앱 알림은 아직 나가지 않습니다 — 직접 전해 주세요'` 였습니다.
 * 운영에서 `OPS_ORDER_PUSH_ENABLED=true` 로 켠 뒤에도 그 문장을 두면, 원장님은
 * 이미 알림을 받은 강사에게 같은 말을 한 번 더 전화로 합니다. 안 나간다고 적힌
 * 화면을 믿기 때문입니다.
 *
 * ⚠️ **웹은 푸시가 켜졌는지 확정할 수 없습니다.** 서버에 그걸 알려주는 API 가
 *    없습니다. 발송은 서버에서만 판정되고, 게이트가 셋입니다
 *    (rappo `opsCoachOrder.service.notifyCoachOrder`).
 *      1. `order.coachVisibleAt` 이 찍혔는가 (생성 시점의 센터 화이트리스트)
 *      2. `isCoachVisibleCenter(centerId)` — `OPS_ORDER_COACH_CENTER_IDS`
 *      3. `config.opsBriefing.coachPushEnabled` — `OPS_ORDER_PUSH_ENABLED`
 *    셋 다 통과해도 강사가 알림을 껐거나 기기 토큰이 없으면 안 갑니다.
 *    그래서 아래 문구는 "전송됩니다"까지만 말하고, 닿지 않을 수 있다는 단서를
 *    함께 답니다. 이 자리에서 "강사가 알림을 받았습니다"라고 단정하면 안 됩니다.
 *
 *    **근본 해법은 서버가 알려주는 것입니다.** access 응답(`GET .../ops-briefing/access`)에
 *    `pushEnabled: boolean` 을 실어 주면 웹이 그 값을 읽어 문구를 갈라 쓸 수 있습니다.
 *    요청은 BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md §4 에 적어 두었습니다.
 *    그때까지는 이 상수가 운영 설정(2026-08-26 ON)을 손으로 따라갑니다.
 */
export const COACH_PUSH_NOTICE = '보내면 강사 앱으로 푸시 알림이 전송됩니다.';

/** 위 문장이 "무조건 닿는다"로 읽히지 않게 하는 단서 */
export const COACH_PUSH_CAVEAT =
    '강사가 앱 알림을 껐거나 기기가 등록되지 않았다면 닿지 않을 수 있습니다.';

/** 확인 창이 받는 한 건의 지시 */
export type OrderIntent = {
    memberId: string;
    memberName: string;
    /** 담당 강사. 없으면 보낼 곳이 없어 확인 창 자체가 열리지 않는다 */
    coachId?: string;
    coachName?: string;
    /**
     * 강사가 받을 일 — **화면이 원장님께 보여준 문장 그대로**.
     * 확인 창에서 요약하거나 줄이지 않습니다. 원장이 읽고 누른 문장과
     * 강사가 받을 문장이 다르면 나중에 "나는 그런 지시 한 적 없는데"가 됩니다.
     */
    task: string;
    /**
     * 회원에게 건넬 문구 — **확인 창은 이 값을 만들지도 고치지도 않습니다.**
     *
     * 오늘의 브리핑은 카드에서 원장이 이미 보고(필요하면 고치고) 누른 초안이 여기로
     * 들어옵니다. 그 화면이 "고친 내용 그대로 전달됩니다"라고 약속했으므로 그대로 싣습니다.
     *
     * 회원 현황에는 초안이 없습니다. 그때는 **키 자체를 보내지 않습니다** —
     * 서버는 키가 없으면 브리핑 항목 초안으로 채우고(없으면 null), null 이면
     * 강사 앱이 회원 이름과 근거로 첫 마디를 만듭니다.
     * 빈 문자열을 넣어 null 로 덮어쓰는 것과는 다른 길입니다.
     */
    memberDraft?: string;
    /**
     * 미리보기 전용 — 브리핑 항목의 초안 원본.
     *
     * **요청 본문에는 실리지 않습니다.** 원장이 카드에서 초안을 통째로 지우고
     * 보내면 위 `memberDraft` 가 빈 값이 되고, 그러면 요청에서 키가 빠지고,
     * 서버는 키가 없을 때 **브리핑 항목의 초안으로 도로 채웁니다**
     * (rappo `opsBriefing.service.resolveMemberDraft`). 즉 강사가 보는 문구는
     * 지워지지 않고 원본이 그대로 갑니다. 미리보기가 이걸 모르면 "앱이 첫 마디를
     * 만듭니다"라고 잘못 말합니다 — 그래서 그 원본을 여기로 함께 받습니다.
     */
    sourceDraft?: string;
    /**
     * 미리보기 전용 — 왜 이 회원인가.
     * 브리핑 항목의 `recommendation.reason` 이 그대로 `reasonSnapshot` 이 됩니다.
     * 회원 현황 경로에는 없습니다(서버가 없는 근거를 지어내지 않습니다).
     */
    reason?: string;
    /** 미리보기 전용 — 근거 수치. 위와 같이 브리핑 경로에만 있습니다 */
    facts?: BriefingFact[];
    /**
     * 원장이 **강사에게** 덧붙일 말 — 회원에게는 가지 않습니다.
     * 대부분 비어서 열리고, 원장이 이 창에서 직접 적습니다.
     *
     * ⚠️ 여기에 회원용 문구를 넣지 마세요. 강사 앱이 이 값을 '원장님 메모'로 띄웁니다.
     */
    ownerNote: string;
    /** 산정 불가면 null (0 원과 다릅니다) */
    valueAmount: number | null;
    valueKind: 'DEFEND' | 'GAIN';
    sourceItemId?: string;
    sourceDate?: DateKst;
    /** "3일 전 브리핑 건입니다" 처럼 확인 창과 완료 알림에 함께 적을 한 줄 */
    contextNote?: string;
};

const failMessage = (error: unknown): string => {
    const msg = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
        ?.message;
    return msg || '지시를 보내지 못했습니다. 잠시 후 다시 시도해 주세요';
};

/**
 * 이 회원에게 이미 나가 있는 지시.
 *
 * 서버는 중복을 막지 않습니다 — 인덱스가 unique 가 아닙니다.
 * 그래서 화면이 알려주는 것 말고는 원장님이 알 방법이 없습니다.
 * 확인 완료(ARCHIVED)된 건은 이미 정리한 건이라 세지 않습니다.
 */
export const priorOrdersFor = (orders: OrderDto[], memberId: string): OrderView[] =>
    toOrderViews(orders.filter((o) => o.memberId === memberId && o.status !== 'ARCHIVED')).sort(
        (a, b) => a.sentDayOffset - b.sentDayOffset
    );

/** 이 회원 건으로 가장 최근에 나간 지시. 없으면 null */
export const latestOrderFor = (orders: OrderDto[], memberId: string): OrderView | null =>
    priorOrdersFor(orders, memberId)[0] ?? null;

/* ═══════════════════════════════════════════════════════════
   확인 창
   ═══════════════════════════════════════════════════════════ */

type ModalProps = {
    intent: OrderIntent;
    /** 같은 회원에게 이미 나가 있는 지시들 */
    prior: OrderView[];
    sending: boolean;
    /** 실패했을 때 창을 닫지 않고 여기에 적는다 — 닫히면 보냈는지 안 보냈는지 모른다 */
    error: string | null;
    onCancel: () => void;
    /** 이 창에서 고칠 수 있는 것은 강사에게 덧붙일 말 하나뿐이다 */
    onConfirm: (edited: { ownerNote: string }) => void;
};

export const OrderConfirmModal = ({
    intent,
    prior,
    sending,
    error,
    onCancel,
    onConfirm,
}: ModalProps) => {
    /** 강사만 볼 글 (선택) — 이 창에서 고칠 수 있는 유일한 값 */
    const [note, setNote] = useState(intent.ownerNote);
    const confirmRef = useRef<HTMLButtonElement>(null);
    const coach = intent.coachName ?? '담당 강사';

    /*
     * 미리보기에 그릴 회원 문구 — **서버가 저장할 값과 같아야 한다.**
     *
     * 아래 confirm() 이 요청을 만드는 규칙과 서버의 resolveMemberDraft 를 합치면
     * 이렇게 된다.
     *   · 문구가 있다              → 그 문구가 그대로 저장된다
     *   · 비었는데 브리핑 항목이다  → 서버가 항목 초안으로 도로 채운다 (키가 빠지므로)
     *   · 비었고 항목도 없다        → null → 강사 앱이 첫 마디를 만든다
     * 셋째 경우에만 "앱이 만들어 준다"고 말해야 한다. 둘째까지 그렇게 말하면
     * 원장님은 지운 줄 알지만 실제로는 원본이 그대로 나간다.
     */
    const previewDraft = intent.memberDraft?.trim() || intent.sourceDraft?.trim() || null;

    /*
     * 열리면 주 버튼에 포커스를 준다.
     * 매일 아침 쓰는 화면이라 단계가 하나 늘어난 만큼 손이 덜 가야 한다 —
     * 포커스가 여기 있으면 Enter 한 번으로 끝난다.
     */
    useEffect(() => {
        confirmRef.current?.focus();
    }, []);

    /*
     * Esc 로 닫는다.
     * 이 저장소의 다른 팝업(EvidenceModal·CoachReportModal)에는 Esc 가 없다.
     * 새로 만드는 것에는 넣는다 — 실수로 연 창을 마우스까지 가져가서 닫아야 하면
     * 확인 단계가 관문이 된다.
     */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !sending) onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel, sending]);

    const confirm = () => onConfirm({ ownerNote: note });

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ background: 'rgba(12,22,40,0.55)', zIndex: 2500, padding: 24 }}
            onClick={() => {
                if (!sending) onCancel();
            }}
            role="presentation"
        >
            <div
                className="flex rounded-[18px]"
                style={{
                    width: 924,
                    maxWidth: '100%',
                    maxHeight: '88vh',
                    background: '#FFFFFF',
                    boxShadow: '0 30px 80px rgba(12,22,40,0.35)',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="지시 보내기 전 확인"
            >
                {/*
                  왼쪽 — 보낼 내용을 확인하고 고치는 자리 (예전의 창 전체).
                  오른쪽에 미리보기가 붙어도 이쪽의 흐름은 그대로다:
                  열리면 주 버튼에 포커스, Enter 한 번으로 끝, Esc 로 취소.
                */}
                <div
                    className="flex flex-col"
                    style={{ width: 560, flex: 'none', minWidth: 0, minHeight: 0 }}
                >
                {/* 헤더 — 누구에게, 어느 회원 건인지가 제목이다 */}
                <div style={{ background: NAVY, padding: '20px 26px' }}>
                    <span style={{ fontSize: 19, fontWeight: 800, color: '#FFFFFF' }}>
                        이대로 보낼까요?
                    </span>
                    <div style={{ marginTop: 5 }}>
                        <span style={{ fontSize: 14, color: '#C7D8F2' }}>
                            <b style={{ color: '#FFFFFF' }}>{coach} 강사</b>에게{' '}
                            <b style={{ color: '#FFFFFF' }}>{intent.memberName} 회원</b> 건을 지시합니다
                        </span>
                    </div>
                </div>

                <div style={{ padding: '20px 26px', overflowY: 'auto' }}>
                    {/* 이미 나가 있는 지시 — 서버가 중복을 막지 않으므로 여기서 알려야 한다 */}
                    {prior.length > 0 && (
                        <div
                            className="rounded-[10px]"
                            style={{
                                border: '1px solid #F5D08A',
                                background: '#FFFAF0',
                                padding: '12px 14px',
                                marginBottom: 16,
                            }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#B45309' }}>
                                {intent.memberName} 회원 건은 이미 지시하셨습니다
                            </span>
                            {prior.slice(0, 3).map((o) => (
                                <div key={o.orderId} style={{ marginTop: 6 }}>
                                    <span style={{ fontSize: 13.5, color: '#8A6A2F', lineHeight: '20px' }}>
                                        {o.sentLabel} · {o.coachName} 강사 · “{o.task}”
                                    </span>
                                </div>
                            ))}
                            <div style={{ marginTop: 7 }}>
                                <span style={{ fontSize: 13, color: '#B45309' }}>
                                    그대로 보내시면 강사에게 같은 회원 건이 한 줄 더 생깁니다.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* 무슨 문장이 가는가 — 요약하지 않고 원문 그대로 */}
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                        강사에게 전할 일
                    </span>
                    <div
                        className="rounded-[9px]"
                        style={{
                            marginTop: 7,
                            background: '#F7F9FC',
                            border: `1px solid ${BORDER}`,
                            borderLeft: '3px solid #1A305D',
                            padding: '13px 15px',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        <span style={{ fontSize: 15.5, color: '#16202E', lineHeight: '24px' }}>
                            {intent.task}
                        </span>
                    </div>

                    {/*
                      고칠 수 있는 칸은 하나뿐이다 — 강사에게 덧붙일 말.

                      ─────────────────────────────────────────────────────────
                      왜 「회원에게 보낼 문구」 칸이 없나
                      ─────────────────────────────────────────────────────────
                      **지시는 강사에게 갑니다.** 회원에게 무슨 말로 연락할지는
                      회원을 아는 강사가 정하는 것이 맞습니다 (2026-08 대표 결정).

                      한때 이 자리에 회원용 문구 칸이 있었습니다. 매일 아침 쓰는 화면인데
                      원장이 회원 인사말까지 쓰고 앉아 있게 되고, 안 쓰면 안 쓴 대로
                      "비워 둬도 되나" 싶어 손이 멈췄습니다.

                      회원 현황에서 낸 지시는 이제 `memberDraft` 를 아예 싣지 않습니다.
                      그러면 강사 앱이 회원 이름과 근거로 첫 마디를 만들어 줍니다
                      (서버 opsCoachOrder.service memberDraft 주석 · 앱 draft.ts).
                      오늘의 브리핑에서 낸 지시만, 카드에서 원장이 이미 보고 고친 초안이
                      그대로 실려 갑니다 — 그 화면이 그렇게 약속했기 때문입니다.
                    */}
                    <div className="flex items-baseline" style={{ marginTop: 18, gap: 7 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                            강사에게 덧붙일 말
                        </span>
                        <span
                            className="rounded-[5px]"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: '#5B6B7F',
                                background: '#F1F4F9',
                                padding: '2px 7px',
                            }}
                        >
                            강사만 봅니다 · 선택
                        </span>
                    </div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={TEXT_MAX}
                        aria-label="강사에게 덧붙일 말"
                        placeholder="예: 이번 주 안에 한 번 연락해 주세요. 시간 안 맞으면 옮겨드려도 됩니다."
                        style={{
                            marginTop: 7,
                            width: '100%',
                            background: '#FBFCFE',
                            border: '1px solid #DCE4EE',
                            borderRadius: 9,
                            padding: '11px 14px',
                            fontSize: 14,
                            color: '#3A4757',
                            lineHeight: '21px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            outline: 'none',
                        }}
                    />
                    <div style={{ marginTop: 5 }}>
                        <span style={{ fontSize: 12.5, color: '#93A0AE', lineHeight: '18px' }}>
                            회원에게는 가지 않습니다. {coach} 강사만 봅니다.
                        </span>
                    </div>

                    {/* 판단에 쓰는 곁가지 — 금액과 날짜 맥락 */}
                    {(intent.valueAmount != null || intent.contextNote) && (
                        <div style={{ marginTop: 10 }}>
                            {intent.valueAmount != null && (
                                <div>
                                    {/*
                                      0원은 "0원짜리 회원"이 아니라 금액을 못 구한 수강권이다
                                      (라포 도입 전 이관 건은 단가·결제액이 0 으로 온다).
                                      "0원 (방어)"라고 적으면 원장님은 안 챙겨도 되는 건으로 읽는다.
                                      화면 다른 곳과 같은 말로 통일한다 — memberValue.VALUE_UNAVAILABLE_TEXT.
                                    */}
                                    <span
                                        style={{ fontSize: 13, color: '#5B6B7F' }}
                                        title={
                                            intent.valueAmount > 0 ? undefined : VALUE_UNAVAILABLE_REASON
                                        }
                                    >
                                        이 건에 걸린 금액{' '}
                                        {intent.valueAmount > 0 ? (
                                            <>
                                                <b style={{ color: '#16202E' }}>
                                                    {intent.valueAmount.toLocaleString('ko-KR')}원
                                                </b>{' '}
                                                ({intent.valueKind === 'GAIN' ? '확보' : '방어'})
                                            </>
                                        ) : (
                                            <b style={{ color: '#7C8794' }}>{VALUE_UNAVAILABLE_TEXT}</b>
                                        )}
                                    </span>
                                </div>
                            )}
                            {intent.contextNote && (
                                <div style={{ marginTop: 3 }}>
                                    <span style={{ fontSize: 13, color: '#93A0AE' }}>
                                        {intent.contextNote}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/*
                      알림이 나간다는 사실 — 확정하는 자리에 반드시 있어야 한다.
                      예전에는 붉은 경고 상자였다(안 나간다는 경고였으므로). 이제는
                      정상 동작을 알리는 자리라 색을 낮춘다 — 매일 아침 보는 화면에서
                      붉은 상자는 무언가 잘못됐다는 뜻으로 읽힌다.
                    */}
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 16,
                            border: '1px solid #C9D9F2',
                            background: '#F5F8FD',
                            padding: '12px 14px',
                        }}
                    >
                        <span style={{ fontSize: 13.5, color: '#2C4C8C', lineHeight: '20px' }}>
                            {COACH_PUSH_NOTICE}
                        </span>
                        <div style={{ marginTop: 3 }}>
                            <span style={{ fontSize: 12.5, color: '#7C8794', lineHeight: '18px' }}>
                                {COACH_PUSH_CAVEAT}
                            </span>
                        </div>
                    </div>

                    {/* 실패는 창 안에서 말한다. 창이 닫히면 나갔는지 아닌지 알 수 없다 */}
                    {error && (
                        <div
                            className="rounded-[10px]"
                            style={{
                                marginTop: 14,
                                border: '1px solid #FBD0D3',
                                background: '#FFE8E9',
                                padding: '12px 14px',
                            }}
                            role="alert"
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#C22D38' }}>
                                지시가 나가지 않았습니다
                            </span>
                            <div style={{ marginTop: 4 }}>
                                <span style={{ fontSize: 13.5, color: '#C22D38', lineHeight: '20px' }}>
                                    {error}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* 바닥 — 주 버튼이 오른쪽, 기본 동작 */}
                <div
                    className="flex items-center"
                    style={{
                        borderTop: `1px solid ${BORDER}`,
                        background: '#FBFCFE',
                        padding: '14px 26px',
                        gap: 10,
                    }}
                >
                    <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                        Esc 를 누르면 취소됩니다
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={sending}
                        className="rounded-[10px]"
                        style={{
                            marginLeft: 'auto',
                            height: 46,
                            padding: '0 22px',
                            background: '#F1F4F9',
                            border: 'none',
                            color: '#425063',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: sending ? 'not-allowed' : 'pointer',
                        }}
                    >
                        취소
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={confirm}
                        /*
                         * 키를 누르고 있으면 브라우저가 keydown 을 반복해서 보낸다.
                         * 카드의 주 버튼을 Enter 로 누른 채 손을 떼지 않으면
                         * 방금 열린 이 창의 확정 버튼까지 눌려 버린다 — 확인 단계를
                         * 지나긴 했지만 원장님은 아무것도 읽지 못한 채 지나간 것이다.
                         * 자동 반복만 막는다. 일부러 한 번 누르는 것은 그대로 통한다.
                         */
                        onKeyDown={(e) => {
                            if (e.repeat) e.preventDefault();
                        }}
                        /*
                         * 연타 방지.
                         * 지시 생성은 멱등이 아니라 두 번 나가면 강사 화면에 같은 회원이 두 줄로 뜬다.
                         * 여기 disabled 는 눈에 보이는 1차 방어이고, 실제 잠금은
                         * useOrderConfirm 의 ref 가드다 — isLoading 이 다음 렌더에나 켜지기 때문이다.
                         */
                        disabled={sending}
                        className="rounded-[10px]"
                        style={{
                            height: 46,
                            padding: '0 24px',
                            background: sending ? '#7C8794' : NAVY,
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: 15.5,
                            fontWeight: 700,
                            cursor: sending ? 'progress' : 'pointer',
                        }}
                    >
                        {sending ? '보내는 중…' : error ? '다시 보내기' : `${coach} 강사에게 지시하기`}
                    </button>
                </div>
                </div>
                {/* ↑ 여기까지가 왼쪽(보낼 내용). 안쪽 들여쓰기는 예전 그대로 두었다 —
                    한 단계 밀면 300 줄이 통째로 diff 에 잡혀 무엇이 바뀌었는지 안 보인다 */}

                {/*
                  오른쪽 — 강사 화면 미리보기.

                  왼쪽에서 적는 「강사에게 덧붙일 말」이 그대로 「관리자님 메모」로
                  올라간다. 그래서 원장님은 "이 말이 강사 화면 어디에 어떻게 뜨는지"를
                  적으면서 본다.

                  문구(memberDraft)는 이 창이 만지지 않으므로 여기서도 고정이다.
                  값이 없는 경우의 처리는 아래 draft 계산과 CoachOrderPreview 주석 참고.
                */}
                <div
                    className="flex-1"
                    style={{
                        background: '#16253F',
                        padding: '18px 14px 22px',
                        minWidth: 0,
                        minHeight: 0,
                        /*
                         * 가로도 스크롤을 연다. 폰 목업은 폭이 고정이라(302),
                         * 좁은 창에서 이 칸이 줄면 잘려 나간다. 잘리는 것보다 미는 편이 낫다.
                         */
                        overflow: 'auto',
                    }}
                >
                    <CoachOrderPreview
                        memberName={intent.memberName}
                        coachName={coach}
                        task={intent.task}
                        ownerNote={note}
                        memberDraft={previewDraft}
                        reason={intent.reason}
                        facts={intent.facts}
                    />
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   두 화면이 공유하는 흐름
   ═══════════════════════════════════════════════════════════ */

type Options = {
    /** 완료·실패를 화면에 알릴 방법 (각 화면의 토스트) */
    notify: (message: string) => void;
    /** "어디서 확인하면 되는지" — 화면마다 진행 현황의 위치가 다르다 */
    tracker: string;
};

export const useOrderConfirm = (ctx: OpsContext, { notify, tracker }: Options) => {
    const [intent, setIntent] = useState<OrderIntent | null>(null);
    const [error, setError] = useState<string | null>(null);
    const createOrder = useCreateOpsOrderMutation(ctx);
    /*
     * 이미 나가 있는 지시를 확인 창과 목록에서 함께 씁니다.
     * 오늘의 브리핑은 OrderTracker 가 같은 캐시를 이미 쓰고 있어 요청이 늘지 않습니다.
     */
    const ordersQuery = useOpsOrdersQuery(ctx, ctx.ready);
    const orders = ordersQuery.data ?? [];

    /**
     * 확정 버튼이 두 번 눌리는 것을 막는다.
     *
     * mutation.isLoading 만으로는 부족하다 — 같은 렌더 안에서 두 번 누르면
     * isLoading 이 아직 false 라 두 건이 나간다. 지시 생성은 멱등이 아니다.
     */
    const sendingRef = useRef(false);

    /** 확인 창을 연다. 담당 강사가 없으면 아예 열지 않는다 — 보낼 곳이 없다 */
    const ask = (next: OrderIntent) => {
        if (!next.coachId) return;
        if (sendingRef.current) return;
        sendingRef.current = false;
        setError(null);
        setIntent(next);
    };

    const cancel = () => {
        if (sendingRef.current) return;
        setError(null);
        setIntent(null);
    };

    const confirm = ({ ownerNote }: { ownerNote: string }) => {
        if (!intent || !intent.coachId) return;
        if (sendingRef.current) return;
        sendingRef.current = true;
        setError(null);

        /*
         * 회원에게 보낼 문구.
         *
         * 이 창은 문구를 만들지도 고치지도 않습니다(대표 결정 — 지시는 강사에게).
         * 그래서 **화면이 원장에게 이미 보여준 초안이 있을 때만** 그대로 싣습니다.
         *
         *  · 오늘의 브리핑  카드에서 보고(고치고) 누른 초안 → 그대로 실어 보낸다.
         *                   그 화면이 "고친 내용 그대로 전달됩니다"라고 약속했다.
         *  · 회원 현황      초안이 없다 → **키를 아예 보내지 않는다.**
         *                   서버는 키가 없으면 브리핑 초안으로 채우는데(없으면 null),
         *                   이 경로는 브리핑 항목이 아니라 결국 null 이 되고,
         *                   그러면 강사 앱이 회원 이름과 근거로 첫 마디를 만든다.
         *
         * 빈 문자열을 실어 보내면 안 됩니다 — 그건 "초안을 지워라"라는 뜻이라
         * 브리핑에서 나온 지시의 초안까지 지워 버립니다.
         */
        const draft = intent.memberDraft?.trim();

        const body: CreateOrderRequest = {
            memberId: intent.memberId,
            coachId: intent.coachId,
            task: intent.task,
            ownerNote,
            ...(draft ? { memberDraft: draft } : {}),
            valueAmount: intent.valueAmount,
            valueKind: intent.valueKind,
            ...(intent.sourceItemId ? { sourceItemId: intent.sourceItemId } : {}),
            ...(intent.sourceDate ? { sourceDate: intent.sourceDate } : {}),
        };

        createOrder.mutate(body, {
            // 성공 알림은 반드시 onSuccess 에서. 서버가 거절했는데 먼저 띄우면 거짓말이 된다.
            onSuccess: () => {
                sendingRef.current = false;
                setIntent(null);
                const suffix = intent.contextNote ? ` · ${intent.contextNote}` : '';
                notify(
                    `${intent.coachName ?? '담당 강사'} 강사에게 ${intent.memberName} 회원 건을 지시했습니다 · ${tracker}${suffix}`
                );
            },
            // 실패하면 창을 닫지 않는다. 닫으면 나갔는지 아닌지 알 방법이 없다.
            onError: (e) => {
                sendingRef.current = false;
                setError(failMessage(e));
            },
        });
    };

    const modal = intent ? (
        <OrderConfirmModal
            key={`${intent.memberId}:${intent.sourceItemId ?? ''}`}
            intent={intent}
            prior={priorOrdersFor(orders, intent.memberId)}
            sending={createOrder.isLoading}
            error={error}
            onCancel={cancel}
            onConfirm={confirm}
        />
    ) : null;

    return {
        /** 확인 창 열기 — 지시는 여기를 지나야만 나간다 */
        ask,
        /** 화면 맨 아래에 그대로 놓으면 된다 */
        modal,
        /** 서버 왕복 중인가 — 목록 버튼을 함께 잠글 때 쓴다 */
        sending: createOrder.isLoading,
        /** 이 회원에게 이미 나가 있는 지시 (없으면 null) */
        sentOrderFor: (memberId: string) => latestOrderFor(orders, memberId),
        orders,
    };
};

export default OrderConfirmModal;
